import { useEffect, useState } from "react";
import { Download, Settings, SquarePlus, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

let deferred = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js").catch(() => {}));
}

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="h-7 w-7 shrink-0 rounded-full bg-[#CC1A00] text-white text-sm font-bold grid place-items-center">{n}</span>
    <span className="pt-0.5 text-sm text-neutral-700 flex-1">{children}</span>
    {Icon && <Icon size={18} className="mt-1 text-neutral-500" />}
  </li>
);

export default function InstallApp() {
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const done = () => setInstalled(true);
    window.addEventListener("appinstalled", done);
    return () => window.removeEventListener("appinstalled", done);
  }, []);

  if (installed) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      if (outcome === "accepted") setInstalled(true);
      return;
    }
    if (isIOS()) window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/app/mlobox.mobileconfig`;
    setOpen(true);
  };

  const ios = isIOS();
  return (
    <>
      <button data-testid="install-app-btn" onClick={install}
        className="flex items-center gap-1.5 bg-white text-[#CC1A00] hover:bg-white/90 transition-colors rounded-full px-3 py-1.5 text-xs font-bold shadow">
        <Download size={15} /> Get the app
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="install-app-dialog" className="max-w-sm">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="MLOBOX" className="h-12 w-12 rounded-xl" />
              <DialogTitle className="font-display">Add MLOBOX to your phone</DialogTitle>
            </div>
            <DialogDescription>Get a MLOBOX icon on your home screen and order in one tap.</DialogDescription>
          </DialogHeader>
          {ios ? (
            <ol data-testid="install-steps-ios" className="space-y-3">
              <Step n={1} icon={Download}>Tap <b>Allow</b> to download MLOBOX.</Step>
              <Step n={2} icon={Settings}>Open <b>Settings</b> and tap <b>Profile Downloaded</b>.</Step>
              <Step n={3}>Tap <b>Install</b>. The MLOBOX icon appears on your home screen.</Step>
            </ol>
          ) : (
            <ol data-testid="install-steps-android" className="space-y-3">
              <Step n={1} icon={MoreVertical}>Open this page in <b>Chrome</b> and tap the <b>menu</b> (three dots).</Step>
              <Step n={2} icon={SquarePlus}>Tap <b>Install app</b> or <b>Add to Home screen</b>.</Step>
              <Step n={3}>Tap <b>Install</b>. The MLOBOX icon appears on your home screen.</Step>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
